import React, { useEffect, useRef, useState } from 'react';

type DailyTask = {
  time?: string;
  title: string;
  description: string;
  category: 'arms-race' | 'wanted-boss' | 'alliance-duel' | 'general' | 'maintenance' | 'versus';
  priority: 'high' | 'medium' | 'low';
};

type DaySchedule = {
  dayName: string;
  dayIndex: number; // 0 = Monday, 6 = Sunday
  tasks: DailyTask[];
};

const getArmsRaceTheme = (dayIndex: number, timeSlot: number): string => {
  const schedule = [
    ['Hero Advancement', 'Base Building', 'Unit Progression', 'Tech Research', 'Drone Boost', 'Hero Advancement'],
    ['Base Building', 'Unit Progression', 'Tech Research', 'Drone Boost', 'Hero Advancement', 'Base Building'],
    ['Unit Progression', 'Tech Research', 'Drone Boost', 'Hero Advancement', 'Base Building', 'Unit Progression'],
    ['Tech Research', 'Drone Boost', 'Hero Advancement', 'Base Building', 'Unit Progression', 'Tech Research'],
    ['Drone Boost', 'Hero Advancement', 'Base Building', 'Unit Progression', 'Tech Research', 'Drone Boost'],
    ['Hero Advancement', 'Base Building', 'Unit Progression', 'Tech Research', 'Drone Boost', 'Hero Advancement'],
    ['Base Building', 'Unit Progression', 'Tech Research', 'Drone Boost', 'Hero Advancement', 'Base Building'],
  ];
  return schedule[dayIndex % 7][timeSlot % 6];
};

const getArmsRaceTasks = (dayIndex: number): DailyTask[] => {
  const times = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'];
  return times.map((time, index) => ({
    time,
    title: `Arms Race: ${getArmsRaceTheme(dayIndex, index)}`,
    description: `Focus on ${getArmsRaceTheme(dayIndex, index).toLowerCase()} activities during this 4-hour window to maximize points.`,
    category: 'arms-race',
    priority: 'high',
  }));
};

const getWantedBossTasks = (dayIndex: number): DailyTask[] => {
  // Wanted Boss happens on Monday (1) and Thursday (4)
  const normalizedDay = dayIndex === 0 ? 1 : dayIndex === 3 ? 4 : null;
  if (normalizedDay === null) return [];

  const times = ['00:00', '06:00', '12:00', '18:00'];
  return times.map((time) => ({
    time,
    title: 'Wanted Boss Event',
    description: 'Boss spawns at this time. Use tank heroes for +50% damage bonus. Coordinate with alliance for maximum rewards.',
    category: 'wanted-boss',
    priority: 'high',
  }));
};

const getGeneralTasks = (dayIndex: number): DailyTask[] => {
  const tasks: DailyTask[] = [
    {
      title: 'Daily Check-in',
      description: 'Collect daily login rewards and check alliance mail.',
      category: 'general',
      priority: 'high',
    },
    {
      title: 'Alliance Donations',
      description: 'Donate resources to alliance tech and building projects.',
      category: 'general',
      priority: 'high',
    },
    {
      title: 'Complete Daily Quests',
      description: 'Finish all daily quests for resources and experience.',
      category: 'general',
      priority: 'medium',
    },
    {
      title: 'Alliance War Participation',
      description: 'Participate in active alliance wars and follow leadership strategies.',
      category: 'alliance-duel',
      priority: 'high',
    },
    {
      title: 'Resource Collection',
      description: 'Collect resources from farms, mines, and production buildings.',
      category: 'general',
      priority: 'medium',
    },
    {
      title: 'Hero Training',
      description: 'Train heroes and upgrade equipment to maintain power growth.',
      category: 'general',
      priority: 'medium',
    },
  ];

  // Add alliance duel specific tasks on certain days
  if (dayIndex === 0) {
    // Monday - start of new week
    tasks.push({
      title: 'Weekly Alliance Duel - Check Theme',
      description: 'Review the weekly alliance duel theme and plan your contribution strategy.',
      category: 'alliance-duel',
      priority: 'high',
    });
  }

  // Add Saturday shield reminder
  if (dayIndex === 5) {
    // Saturday - VS day reminder
    tasks.push({
      title: 'VS Day Shield Reminder',
      description: 'If you don\'t want to fight on Saturday, grab a 24-hour shield from the alliance store!',
      category: 'versus',
      priority: 'high',
    });
  }

  return tasks;
};

const getDaySchedule = (dayIndex: number, dayName: string): DaySchedule => {
  const tasks: DailyTask[] = [
    ...getWantedBossTasks(dayIndex),
    ...getArmsRaceTasks(dayIndex),
    ...getGeneralTasks(dayIndex),
  ];

  // Sort tasks by priority and time
  tasks.sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }
    if (a.time && b.time) {
      return a.time.localeCompare(b.time);
    }
    if (a.time) return -1;
    if (b.time) return 1;
    return 0;
  });

  return {
    dayName,
    dayIndex,
    tasks,
  };
};

const weekSchedule: DaySchedule[] = [
  getDaySchedule(0, 'Monday'),
  getDaySchedule(1, 'Tuesday'),
  getDaySchedule(2, 'Wednesday'),
  getDaySchedule(3, 'Thursday'),
  getDaySchedule(4, 'Friday'),
  getDaySchedule(5, 'Saturday'),
  getDaySchedule(6, 'Sunday'),
];

const categoryColors = {
  'arms-race': 'from-primary to-primary-light',
  'wanted-boss': 'from-accent to-yellow-400',
  'alliance-duel': 'from-secondary to-pink-400',
  general: 'from-blue-500 to-blue-400',
  maintenance: 'from-gray-500 to-gray-400',
  versus: 'from-purple-500 to-purple-400',
};

const priorityBadges = {
  high: 'bg-red-500/20 text-red-400 border-red-500/40',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  low: 'bg-green-500/20 text-green-400 border-green-500/40',
};

const DailyTasks: React.FC = () => {
  const dayRefs = useRef<HTMLDivElement[]>([]);
  const [currentDay, setCurrentDay] = useState<number>(0);

  useEffect(() => {
    const now = new Date();
    const dayIndex = (now.getUTCDay() + 6) % 7; // Monday = 0
    setCurrentDay(dayIndex);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    dayRefs.current.forEach((dayCard) => {
      if (dayCard) {
        dayCard.classList.add('fade-in-ready');
        observer.observe(dayCard);
      }
    });

    return () => {
      dayRefs.current.forEach((dayCard) => {
        if (dayCard) observer.unobserve(dayCard);
      });
    };
  }, []);

  return (
    <section className="section section-dark">
      <div className="container">
        <h1 className="section-title">Daily Tasks & Activities</h1>
        <p className="text-center text-text-secondary max-w-3xl mx-auto leading-relaxed mb-8">
          Complete these daily tasks and activities to maximize your contribution to the alliance. Tasks are organized by day
          based on the weekly schedule. All times are in server time (UTC).
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {weekSchedule.map((day, index) => {
            const isToday = day.dayIndex === currentDay;
            return (
              <div
                key={day.dayName}
                className={`bg-bg-card rounded-xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-glow ${
                  isToday ? 'border-primary shadow-glow' : 'border-border hover:border-primary'
                }`}
                ref={(el) => {
                  if (el) dayRefs.current[index] = el;
                }}
              >
                <div
                  className={`px-6 py-4 border-b ${
                    isToday ? 'bg-primary/10 border-primary/40' : 'bg-bg-darker border-border'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h2 className="font-orbitron text-2xl text-primary-light">{day.dayName}</h2>
                    {isToday && (
                      <span className="px-3 py-1 rounded-full text-xs uppercase tracking-wide bg-primary/20 text-primary-light border border-primary/40">
                        Today
                      </span>
                    )}
                  </div>
                  <p className="text-text-muted text-sm mt-1">
                    {day.tasks.length} {day.tasks.length === 1 ? 'task' : 'tasks'}
                  </p>
                </div>

                <div className="divide-y divide-border">
                  {day.tasks.map((task, taskIndex) => (
                    <div key={`${day.dayName}-${taskIndex}`} className="px-6 py-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-text-primary">{task.title}</h3>
                            <span
                              className={`px-2 py-0.5 rounded text-xs uppercase tracking-wide border ${priorityBadges[task.priority]}`}
                            >
                              {task.priority}
                            </span>
                          </div>
                          {task.time && (
                            <div className="font-orbitron text-sm text-primary-light mb-1">{task.time} UTC</div>
                          )}
                          <p className="text-text-secondary text-sm leading-relaxed">{task.description}</p>
                        </div>
                        <div
                          className={`w-2 h-2 rounded-full bg-gradient-to-r ${categoryColors[task.category]} shrink-0 mt-1.5`}
                          title={task.category.replace('-', ' ')}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12">
          <h3 className="text-center font-orbitron text-2xl text-primary-light mb-6">Task Categories</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {Object.entries(categoryColors).map(([category, gradient]) => (
              <div
                key={category}
                className="bg-bg-card rounded-xl border border-border p-4 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-glow"
              >
                <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${gradient} mx-auto mb-2`}></div>
                <div className="font-orbitron text-sm text-primary-light capitalize">
                  {category.replace('-', ' ')}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-bg-card rounded-xl border border-border p-6 max-w-3xl mx-auto">
          <h3 className="font-orbitron text-xl text-primary-light mb-4">Tips for Maximum Efficiency</h3>
          <ul className="space-y-2 text-text-secondary">
            <li className="flex items-start gap-2">
              <span className="text-primary-light shrink-0">•</span>
              <span>
                <strong>Plan ahead:</strong> Check the schedule at the start of each day to prioritize time-sensitive
                events like Arms Race and Wanted Boss.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-light shrink-0">•</span>
              <span>
                <strong>Coordinate with alliance:</strong> Communicate with alliance members about Wanted Boss spawns
                and alliance duel strategies.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-light shrink-0">•</span>
              <span>
                <strong>Focus on themes:</strong> During Arms Race windows, concentrate on the specific theme activity to
                maximize points.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-light shrink-0">•</span>
              <span>
                <strong>Daily consistency:</strong> Complete general tasks every day to maintain steady progress and
                alliance contribution.
              </span>
            </li>
          </ul>
        </div>

        <div className="mt-8 bg-bg-card rounded-xl border border-purple-500/40 p-6 max-w-4xl mx-auto">
          <h3 className="font-orbitron text-xl text-purple-400 mb-6 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-purple-400"></span>
            Versus Mode Guide
          </h3>
          
          <div className="space-y-6">
            <div className="bg-bg-darker rounded-lg border border-purple-500/20 p-5">
              <h4 className="font-orbitron text-lg text-purple-300 mb-3">Day 1 VS - Radar Training Day</h4>
              <div className="space-y-3 text-text-secondary">
                <div>
                  <strong className="text-text-primary">Priority Tasks:</strong>
                  <ul className="mt-2 ml-4 space-y-1 list-disc">
                    <li>Complete Radar Tasks</li>
                    <li>Hero Exp (if needed)</li>
                    <li>Gathering Resources</li>
                    <li>Drone Combat Data</li>
                    <li>Drone Skill Chips</li>
                  </ul>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <strong className="text-text-primary">Daily Schedule:</strong>
                  <ul className="mt-2 ml-4 space-y-1 list-disc">
                    <li>Today is radar training - make sure you have gatherers deployed!</li>
                    <li>As soon as reset happens, wait at least 5 minutes and collect your squads from gathering</li>
                    <li>Complete radar tasks</li>
                    <li>Upgrade your drone</li>
                    <li>Use your drone skill chips</li>
                    <li>If you need the points, upgrade your heroes, but it's better to save for Hero Day (Day 4 of VS)!</li>
                  </ul>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-text-primary italic">Have fun and stay safe!</p>
                </div>
              </div>
            </div>

            <div className="bg-bg-darker rounded-lg border border-purple-500/20 p-5">
              <h4 className="font-orbitron text-lg text-purple-300 mb-3">Day 2 VS - Building Day</h4>
              <div className="space-y-3 text-text-secondary">
                <div>
                  <strong className="text-text-primary">Priority Tasks:</strong>
                  <ul className="mt-2 ml-4 space-y-1 list-disc">
                    <li>Stack radar tasks</li>
                    <li>Construction Speed Ups</li>
                    <li>Increase Build Power - pop all the presents</li>
                    <li>Use Survivor Recruitment Tickets</li>
                    <li>Send Legendary Tasks</li>
                    <li>Send Legendary Trucks</li>
                  </ul>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <strong className="text-text-primary">Daily Schedule:</strong>
                  <ul className="mt-2 ml-4 space-y-1 list-disc">
                    <li>Today is building day</li>
                    <li>Open all of your presents! Start a build and use your construction speed ups!</li>
                    <li>Recruit your survivors</li>
                    <li>Send out legendary tasks (those are your gold ones) - if you don't seem to have any, use your task refresh tickets</li>
                    <li>Send out legendary trucks (those are your gold ones) - if you don't seem to have any, use your truck refresh tickets</li>
                  </ul>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-text-primary italic">Have fun and stay safe!</p>
                </div>
              </div>
            </div>

            <div className="bg-bg-darker rounded-lg border border-purple-500/20 p-5">
              <h4 className="font-orbitron text-lg text-purple-300 mb-3">Day 2 VS Prep (Before Day 2)</h4>
              <div className="space-y-2 text-text-secondary">
                <p><strong className="text-text-primary">Save radar tasks!</strong> Start upgrading things and leave them in the presents.</p>
                <ul className="mt-2 ml-4 space-y-1 list-disc">
                  <li>Save or collect construction speed ups</li>
                  <li>Save survivor tickets</li>
                </ul>
              </div>
            </div>

            <div className="bg-bg-darker rounded-lg border border-purple-500/20 p-5">
              <h4 className="font-orbitron text-lg text-purple-300 mb-3">Day 3 VS Prep (Before Day 3)</h4>
              <div className="space-y-2 text-text-secondary">
                <ul className="ml-4 space-y-1 list-disc">
                  <li>Start research that will finish at least 5 mins after reset</li>
                  <li>Save those valor badges and drone chests</li>
                </ul>
              </div>
            </div>

            <div className="bg-bg-darker rounded-lg border border-purple-500/20 p-5">
              <h4 className="font-orbitron text-lg text-purple-300 mb-3">Day 3 VS - Research Day</h4>
              <div className="space-y-3 text-text-secondary">
                <div>
                  <strong className="text-text-primary">Priority Tasks:</strong>
                  <ul className="mt-2 ml-4 space-y-1 list-disc">
                    <li>Research Speed Ups</li>
                    <li>Increase Research Power (finish a research)</li>
                    <li>Use Valor Badges</li>
                    <li>Radar Tasks</li>
                    <li>Open Drone Component chests</li>
                  </ul>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <strong className="text-text-primary">Daily Schedule:</strong>
                  <ul className="mt-2 ml-4 space-y-1 list-disc">
                    <li>Today is research day</li>
                    <li>Finish research for points today! That can be collecting finished research!</li>
                    <li>Use the valor badges - you can use those in the alliance duel section in the research tree</li>
                    <li>Do those radar tasks and open those drone chests!</li>
                  </ul>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-text-primary italic">Have fun and stay safe!</p>
                </div>
              </div>
            </div>

            <div className="bg-bg-darker rounded-lg border border-purple-500/20 p-5">
              <h4 className="font-orbitron text-lg text-purple-300 mb-3">Day 4 VS Prep (Before VS Day 4)</h4>
              <div className="space-y-3 text-text-secondary">
                <div>
                  <strong className="text-text-primary">1) Save Hero XP</strong>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>Don't use hero XP during the week</li>
                    <li>Save it to level up multiple heroes on Day 4</li>
                  </ul>
                </div>
                <div>
                  <strong className="text-text-primary">2) Save Hero Shards</strong>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>Hold off on promoting heroes</li>
                    <li>Promoting a hero (especially purple or gold) gives huge points</li>
                  </ul>
                </div>
                <div>
                  <strong className="text-text-primary">3) Save Skill Medals</strong>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>Use them to upgrade hero skills on Day 4</li>
                    <li>Focus on combat or support heroes for best value</li>
                  </ul>
                </div>
                <div>
                  <strong className="text-text-primary">4) Check Arms Race Sync</strong>
                  <ul className="mt-1 ml-4 space-y-1 list-disc">
                    <li>If Arms Race includes hero upgrades, you'll get double rewards</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-bg-darker rounded-lg border border-purple-500/20 p-5">
              <h4 className="font-orbitron text-lg text-purple-300 mb-3">Day 4 VS - Hero Development Day</h4>
              <div className="space-y-4 text-text-secondary">
                <div>
                  <strong className="text-text-primary">What Day 4 Is About</strong>
                  <p className="mt-1">VS Day 4 focuses on Hero Development, where you earn points by:</p>
                  <ul className="mt-2 ml-4 space-y-1 list-disc">
                    <li>Leveling up heroes</li>
                    <li>Promoting heroes (using hero shards)</li>
                    <li>Upgrading hero skills</li>
                    <li>Using hero XP and skill medals</li>
                  </ul>
                </div>
                <div className="pt-3 border-t border-border">
                  <strong className="text-text-primary">Execution (During VS Day 4)</strong>
                  <div className="mt-2 space-y-3">
                    <div>
                      <strong className="text-text-primary">1) Level Up Heroes</strong>
                      <ul className="mt-1 ml-4 space-y-1 list-disc">
                        <li>Use saved hero XP to level up your strongest heroes</li>
                        <li>Prioritize heroes that are close to leveling for quick points</li>
                      </ul>
                    </div>
                    <div>
                      <strong className="text-text-primary">2) Promote Heroes</strong>
                      <ul className="mt-1 ml-4 space-y-1 list-disc">
                        <li>Use saved hero shards to promote heroes to the next star level</li>
                        <li>Gold and purple heroes give the most points</li>
                      </ul>
                    </div>
                    <div>
                      <strong className="text-text-primary">3) Upgrade Skills</strong>
                      <ul className="mt-1 ml-4 space-y-1 list-disc">
                        <li>Use skill medals to upgrade hero abilities</li>
                        <li>Focus on skills that improve troop performance or base defense</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="pt-3 border-t border-border">
                  <strong className="text-text-primary">Extra Tips</strong>
                  <ul className="mt-2 ml-4 space-y-1 list-disc">
                    <li><strong>Hero Recruitment:</strong> If you have saved recruitment tickets, use them to get more shards</li>
                    <li><strong>Don't Waste XP on Low-Tier Heroes:</strong> Focus on UR gold heroes for best point return</li>
                    <li><strong>Campaign:</strong> Participate in the Campaign in the Alert Tower for extra XP and Campaign points</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-bg-darker rounded-lg border border-purple-500/20 p-5">
              <h4 className="font-orbitron text-lg text-purple-300 mb-3">Day 5 VS - Troop Day</h4>
              <div className="space-y-3 text-text-secondary">
                <div>
                  <strong className="text-text-primary">Priority Tasks:</strong>
                  <ul className="mt-2 ml-4 space-y-1 list-disc">
                    <li>Radar Tasks</li>
                    <li>Train Troops</li>
                    <li>Use Speed Ups in Construction & Research & Troops</li>
                  </ul>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <strong className="text-text-primary">Daily Schedule:</strong>
                  <ul className="mt-2 ml-4 space-y-1 list-disc">
                    <li>Today is troop day</li>
                    <li>Train as many troops as possible - use those speed ups to help make that happen!</li>
                    <li>If you have smaller troops, upgrade them to the highest tier - go to the smaller troop, click the green arrow then promote!</li>
                    <li>If you have any speed ups burning a hole in your pocket, today is the day to use them!</li>
                  </ul>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-text-primary italic">Have fun and stay safe!</p>
                </div>
              </div>
            </div>

            <div className="bg-bg-darker rounded-lg border border-purple-500/20 p-5">
              <h4 className="font-orbitron text-lg text-purple-300 mb-3">Day 6 VS Prep</h4>
              <div className="space-y-2 text-text-secondary">
                <ul className="ml-4 space-y-1 list-disc">
                  <li>Save truck tickets</li>
                  <li>Save task tickets</li>
                  <li>Grab a shield!</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-500/10 rounded-lg border border-yellow-500/40 p-4 mt-4">
              <p className="text-yellow-400 font-semibold mb-2">⚠️ Important Reminder</p>
              <p className="text-text-secondary">
                If you don't want to fight on Saturday, grab a 24-hour shield from the alliance store!
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DailyTasks;

